import {
  getCompany,
  getHistoricMaintances,
  getItemsMaintances,
  getVehicles,
} from '../utils/request';

// Um registro do historico, igual para as duas origens:
// 'qc' (legado Querido Carro) e 'os' (OS Digital). So usa campos que a API ja devolve.

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const joinAddress = (street, number, neighborhood) => {
  const line = [street, number].filter(Boolean).join(', ');
  return [line, neighborhood].filter(Boolean).join(' - ');
};

export function normalizeQcSale(entry) {
  const sale = entry.sale || {};
  const company = entry.Company || {};
  const total = toNumber(sale.Orctotved);
  const discount = toNumber(sale.Orcdescto);

  return {
    key: `qc-${sale.Orcodigo}-${onlyDigits(company.OfCnpjCpf)}`,
    source: 'qc',
    id: sale.Orcodigo,
    code: sale.Orcodigo,
    date: sale.Orcdata ? new Date(sale.Orcdata) : null,
    plate: sale.DadChapa,
    vehicleLabel: sale.OrcObjeto || '',
    km: toNumber(sale.DadKm),
    total,
    discount,
    subtotal: total !== null && discount !== null ? total + discount : null,
    company: {
      name: company.OfNomeFan || company.OfRzSocia || 'Empresa não informada',
      cnpj: onlyDigits(company.OfCnpjCpf),
      phone: onlyDigits(`${company.OfDDD || ''}${company.OfTel || ''}`),
      address: joinAddress(company.OfEndere, company.OfNumero, company.OfBairro),
      city: [company.OfCidade, company.OfEstado].filter(Boolean).join(' - '),
    },
    photos: [],
    evaluation: entry.evaluated || null,
    evaluated: Boolean(entry.evaluated),
    // Avaliacao de venda QC esta desligada na API; o registro so mostra a ja feita.
    canEvaluate: false,
  };
}

export function normalizeOsSale(sale) {
  const company = sale.Company || {};
  const phone = company.Phones && company.Phones[0]
    ? company.Phones[0].Number_Phone1 || company.Phones[0].Number_Phone2
    : '';

  return {
    key: `os-${sale.id}`,
    source: 'os',
    id: sale.id,
    code: sale.Code,
    date: sale.Date ? new Date(sale.Date) : null,
    plate: sale.License_Plate,
    vehicleLabel: (sale.Vehicle && sale.Vehicle.Model) || '',
    km: toNumber(sale.Kilometers),
    total: toNumber(sale.Final_Value),
    discount: toNumber(sale.Discount_Value),
    subtotal: toNumber(sale.SubTotal),
    company: {
      id: company.id,
      name: company.Trading_Name || 'Empresa não informada',
      cnpj: onlyDigits(company.Cpf_Cnpj),
      phone: onlyDigits(phone),
      address: '',
      city: '',
    },
    photos: (sale.SalesPhotos || []).filter((photo) => photo && photo.link),
    evaluation: null,
    evaluated: Boolean(sale.evaluated),
    canEvaluate: !sale.evaluated,
  };
}

const byDateDesc = (a, b) => (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0);

export function normalizeHistory(payload) {
  const data = payload && !Array.isArray(payload) ? payload : {};
  return [
    ...(data.qcSales || []).map(normalizeQcSale),
    ...(data.osSales || []).map(normalizeOsSale),
  ].sort(byDateDesc);
}

/**
 * Veiculos do cliente com o historico de cada um. Uma consulta por placa, em paralelo;
 * a placa que falhar fica marcada e nao derruba as outras.
 */
export async function loadGarage() {
  const vehiclesResult = await getVehicles();
  if (vehiclesResult.message) {
    return { vehicles: [], error: 'Não foi possível carregar seus veículos. Atualize a página para tentar de novo.' };
  }

  const vehicles = Array.isArray(vehiclesResult.payload) ? vehiclesResult.payload : [];
  const histories = await Promise.all(
    vehicles.map((vehicle) => getHistoricMaintances(vehicle.licensePlate)),
  );

  return {
    error: null,
    vehicles: vehicles.map((vehicle, index) => {
      const result = histories[index] || {};
      const failed = result.type === 'HANDLE_HISTORIC_MAINTANCES_FAILURE';
      const records = failed ? [] : normalizeHistory(result.payload);
      const pendingEvaluations = records.filter((record) => record.canEvaluate).length;
      return {
        ...vehicle,
        records,
        failed,
        lastRecord: records[0] || null,
        pendingEvaluations,
      };
    }),
  };
}

const describeCoverage = (kilometers, months) => {
  const parts = [];
  if (kilometers) parts.push(`${kilometers} km`);
  if (months) parts.push(`${months} meses`);
  return parts.join(' ou ');
};

// Itens do atendimento separados em pecas e servicos.
export function normalizeItems(record, items) {
  const list = Array.isArray(items) ? items : [];

  if (record.source === 'os') {
    return list.map((item) => {
      const isProduct = Boolean(item.Product_id);
      const catalog = (isProduct ? item.Products : item.Services) || {};
      return {
        kind: isProduct ? 'product' : 'service',
        description: item.Description,
        quantity: toNumber(item.Quantity),
        unitValue: toNumber(item.Unit_Value),
        discount: toNumber(item.Discount_Value),
        total: toNumber(item.Amount),
        warranty: catalog.Warranty ? describeCoverage(catalog.Warranty.Kilometers, catalog.Warranty.Months) : '',
        review: catalog.Review ? describeCoverage(catalog.Review.Km, catalog.Review.Months) : '',
      };
    });
  }

  // QC: TipoItem nao tem dominio documentado; sem ele confiavel, os itens ficam numa lista so.
  return list.map((item) => ({
    kind: 'item',
    description: item.Descricao,
    quantity: toNumber(item.Ociquanti),
    unitValue: toNumber(item.OciprcTab),
    discount: toNumber(item.Ocidescto),
    total: toNumber(item.Ocisubtot),
    warranty: item.OciGarKm ? `${item.OciGarKm} km` : '',
    review: item.OciKmRevi ? `${item.OciKmRevi} km` : '',
  }));
}

/** Itens do atendimento e, no OS Digital, o endereco da empresa. */
export async function loadRecordDetail(record) {
  const itemsRequest = record.source === 'os'
    ? getItemsMaintances('', '', record.id)
    : getItemsMaintances(record.code, record.company.cnpj, record.id);
  const companyRequest = record.source === 'os' && record.company.id
    ? getCompany(record.company.id)
    : Promise.resolve(null);

  const [itemsResult, companyResult] = await Promise.all([itemsRequest, companyRequest]);
  const companyData = companyResult && !companyResult.message ? companyResult.payload || {} : {};

  return {
    items: normalizeItems(record, itemsResult.payload),
    address: record.source === 'os'
      ? joinAddress(companyData.Address, companyData.Address_Number, companyData.Neighborhood)
      : record.company.address,
  };
}

// Formato que o modal de avaliacao (EvaluationView/evaluationAction) ja espera.
export function toEvaluationValues(record) {
  return {
    idManutencao: record.id,
    OS: record.code,
    Date: record.date ? record.date.toISOString() : null,
    Company: record.company.name,
    Cpf_Cnpj: record.company.cnpj,
    Type: record.source,
    Avaliacao: record.source === 'qc' ? record.evaluation || false : record.evaluated,
  };
}
